import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, makeStyles, Select, MenuItem, Popover, Tooltip } from "@material-ui/core";

import { DropdownChevronIcon as DropdownChevron } from "../../../../../drawer/components/Primitives/icons/DropdownChevronIcon";

import { TldrawUiIcon, TLShapeId, useEditor, useValue } from "@tldraw/tldraw";
import { DocumentOrientation, TemplateConfig, PossibleFlyerType } from "pages/flyer/builder/drawer/types";
import { ADMDocumentFormats, DMDocumentFormats, DocumentFormats, CAMPAIGN_SUBTYPES_ENUM } from "shared/constants";
import { generatePath, useParams, useLocation } from "react-router-dom";
import { getFlyerWeightOptions, isValidFlyerType } from "utils/isValidFlyerType";
import ROUTES from "config/routing";
import { useHistory } from "react-router";
import useUpdateAssets from "Hooks/updateAsset";
import { FORMATS } from "../../../../../constants";
import { getEffectiveFormat } from "../../../../../formats/formatsStore";
import { useRuntimeFormats } from "../../../../../formats/useFormats";
import { defaultOrientationFor } from "../../../../../formats/orientation";
import { useTldrawApp } from "../../../../../drawer/hooks";
import { useStore } from "../../../../../../../../store";
import { PrintingCostsType, StoreType } from "../../../../../../../../store/types";
import useCalculating from "../../../../../../../../Hooks/useCalculating";
import { updateCampaignExtraData, updateCampaign as updateCampaignGQL } from "../../../../../../../../graphQL";
import { getCampaign } from "../../../../../../../../graphQL/query/campaign";
import { apiClient } from "module/api";
import {
  getFlyerSizeDisplay,
  displayInInch,
  detectMeasurementSystemFromLocale,
  getOpacityPercentage,
  setOpacityPercentage,
} from "../../../../../utils";
import LinesHint from "./component/LinesHint";
import { useAddressBox, AddressBoxType } from "../../../../../../../../Hooks/useAddressBox";
import { CustomAddressBoxShape } from "pages/flyer/builder/drawer/state/newShapes/CustomAddressBoxUtil";
import useDesignManagement from "pages/creative/pageV2/useDesignManagement";
import uspsLogo from "../../../../../../../../static/media/formats/USPS.png";
import { isADMFormat, isEDDMFormat } from "utils/formats";
import { getStoredOrientation, setStoredOrientation } from "pages/flyer/builder/utils/orientationStorage";
import { authorization } from "module/auth";
import { removeGuestPlaceholders } from "pages/flyer/builder/utils/guestPlaceholders";
import { useIsBuilderEmptyState } from "pages/flyer/builder/hooks/useIsBuilderEmptyState";
import { ADM_FORMAT } from "campaign-builder/domain/constants";
import CustomColorPicker from "../../../../../drawer/components/CustomColorPicker";
import CheckedBoxIcon from "components/icons/components/CheckedBoxIcon";
import UncheckedBoxIcon from "components/icons/components/UncheckedBoxIcon";
import { PlaceAutocomplete } from "components/place-autocomplete";
import { useColorPickerContext } from "../../../../../drawer/components/ColorPickerContext";
import { useDraggableColorPicker } from "../../../../../drawer/components/useDraggableColorPicker";
import { PlusIcon } from "@radix-ui/react-icons";

interface SettingsProps {
  loading: boolean;
}

export const getAllowedFormats = ({
  channel,
  subtype,
  countryCode,
  printingCosts,
  flyerWeight,
  isPublicBuilder,
  workflowUuid,
  catalogueCodes,
}: {
  channel: string;
  subtype?: string | null;
  countryCode: string;
  printingCosts: PrintingCostsType;
  flyerWeight?: string;
  isPublicBuilder: boolean;
  workflowUuid?: string | null;
  // Codes the print-format API offers for this country + channel; null/undefined means use the built-in rules.
  catalogueCodes?: string[] | null;
}) => {
  if (workflowUuid) {
    return Object.keys(ADM_FORMAT);
  }

  // Public flyer builder path (`/design/:countryCode/:roomId/...`) covers
  // both the guest signup canvas and the authed builder a user lands on
  // right after signing up. In both cases the user has no `printingCosts`
  // wired yet, so the normal `isValidFlyerType` filter would only let
  // through TEARAWAY / DOOR_HANGER. We curate the public catalogue
  // ourselves: TEARAWAY + DOOR_HANGER (general flyer formats) plus the
  // full EDDM and ADM families. Internal ADM and EDDM flows keep their
  // dedicated filters via `workflowUuid` and the channel-based branches.
  if (isPublicBuilder) {
    return [
      DocumentFormats.TEARAWAY,
      DocumentFormats.DOOR_HANGER,
      ...Object.values(DMDocumentFormats),
      ...Object.values(ADMDocumentFormats),
    ];
  }

  if (catalogueCodes) {
    return catalogueCodes;
  }

  const allowedFormats = Object.keys(FORMATS).filter((format) => {
    try {
      const isValid = isValidFlyerType({
        countryCode,
        channel: subtype || channel,
        printingCosts,
        flyerType: format,
        flyerWeight,
        isPublicBuilder,
      } as any); // Type assertion to fix type error
      return isValid;
    } catch (error) {
      return true;
    }
  });
  return allowedFormats;
};

// Helper function to check if a format is USPS compliant EDDM format (only for USA)
const isUSPSCompliant = (format: string, countryCode: string): boolean => {
  if (countryCode !== "US") return false;
  const eddmUspsCompliantFormats = [...Object.values(DMDocumentFormats), ...Object.values(ADMDocumentFormats)];
  return eddmUspsCompliantFormats.includes(format);
};

const POSTCARD_FORMATS = new Set<string>([
  ADMDocumentFormats.POSTCARD_6x4,
  ADMDocumentFormats.POSTCARD_9x6,
  ADMDocumentFormats.POSTCARD_11x6,
  ADMDocumentFormats.POSTCARD_15x12,
]);

const LARGE_FAMILY_FORMATS = new Set<string>([
  DMDocumentFormats.SMALL,
  DMDocumentFormats.BEST_SELLER,
  DMDocumentFormats.LARGE,
  DMDocumentFormats.JUMBO,
  DMDocumentFormats.OVERSIZED,
]);

const isPostcardFormat = (format?: string | null) => (format ? POSTCARD_FORMATS.has(format) : false);
const isLargeFamilyFormat = (format?: string | null) => (format ? LARGE_FAMILY_FORMATS.has(format) : false);
const isDoorHangerOrTearaway = (format?: string | null) =>
  format ? format === DocumentFormats.DOOR_HANGER || format === DocumentFormats.TEARAWAY : false;

const BackgroundColorPicker = ({ side }: { side: "front" | "back" }) => {
  const editor = useEditor();
  const [isAdded, setIsAdded] = useState(() => localStorage.getItem(`bg-color-added-${side}`) === "true");
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [currentColor, setCurrentColor] = useState<string>("#ffffff");
  const [opacity, setOpacity] = useState<string>("100");
  const [isEditingOpacity, setIsEditingOpacity] = useState(false);
  const [opacityInput, setOpacityInput] = useState("");
  const opacityInputRef = React.useRef<HTMLInputElement | null>(null);

  const shapeId = (side === "front" ? "shape:front" : "shape:back") as TLShapeId;
  const blackLineId = (side === "front" ? "shape:frontBlackLine" : "shape:backBlackLine") as TLShapeId;
  const label = side === "front" ? "Front Side" : "Back Side";
  const pickerId = `bg-color-${side}`;

  const { openColorPicker, closeColorPicker, isOpen } = useColorPickerContext();

  // Listen for tour close event
  React.useEffect(() => {
    const handler = () => {
      setAnchorEl(null);
      closeColorPicker(pickerId);
    };
    window.addEventListener("tour:close-color-picker", handler);
    return () => window.removeEventListener("tour:close-color-picker", handler);
  }, [closeColorPicker, pickerId]);

  // Reactively watch the shape's fill so undo/redo updates are detected
  const shapeFill = useValue(
    `bg-fill-${side}`,
    () => {
      const shape = editor.getShape(shapeId);
      return (shape?.props as any)?.fill as string | undefined;
    },
    [editor, shapeId]
  );

  useEffect(() => {
    if (shapeFill) {
      setCurrentColor(shapeFill);
      setOpacity(`${getOpacityPercentage(shapeFill)}`);
    }
  }, [shapeFill]);

  const open = Boolean(anchorEl) && isOpen(pickerId);

  useEffect(() => {
    if (!isOpen(pickerId) && anchorEl) setAnchorEl(null);
  }, [isOpen, pickerId, anchorEl]);

  // Drag-by-border + canvas-pan + outside-click-close behaviour
  // shared with the brand-kit and shape color pickers.
  const closePicker = useCallback(() => {
    setAnchorEl(null);
    closeColorPicker(pickerId);
    document.body.dispatchEvent(new CustomEvent("tour:color-picked"));
  }, [closeColorPicker, pickerId]);
  const { paperContentRef, handleBorderMouseDown, isDragging } = useDraggableColorPicker(open, anchorEl, closePicker);

  const updateBackground = useCallback(
    (newColor: string) => {
      editor.run(
        () => {
          const shape = editor.getShape(shapeId);
          if (shape) {
            editor.updateShape({ id: shapeId, type: shape.type, props: { ...shape.props, fill: newColor } });
          }
          const blackLine = editor.getShape(blackLineId);
          if (blackLine) {
            editor.updateShape({
              id: blackLineId,
              type: blackLine.type,
              props: { ...blackLine.props, fill: newColor },
            });
          }
        },
        { ignoreShapeLock: true }
      );
    },
    [editor, shapeId, blackLineId]
  );

  const handleColorChange = useCallback(
    (newColor: string) => {
      setCurrentColor(newColor);
      setOpacity(`${getOpacityPercentage(newColor)}`);
      updateBackground(newColor);
    },
    [updateBackground]
  );

  const handleColorInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCurrentColor(e.target.value.trim());
  };

  const handleColorInputBlur = () => {
    const isLikelyHex = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.test(currentColor);
    if (isLikelyHex) {
      setOpacity(`${getOpacityPercentage(currentColor)}`);
      updateBackground(currentColor);
    } else {
      const shape = editor.getShape(shapeId);
      const fill = (shape?.props as any)?.fill || "#ffffff";
      setCurrentColor(fill);
      setOpacity(`${getOpacityPercentage(fill)}`);
    }
  };

  if (!isAdded) {
    return (
      <div className="flex items-center justify-between">
        <span style={{ fontSize: 14, color: "var(--color-deep-blue)", fontWeight: 400 }}>{label}</span>
        <PlusIcon
          className="h-5 w-5 cursor-pointer text-[#707087]"
          onClick={() => {
            setIsAdded(true);
            localStorage.setItem(`bg-color-added-${side}`, "true");
          }}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1">
      <span style={{ fontSize: 14, color: "var(--color-deep-blue)", fontWeight: 400 }}>{label}</span>
      <div className="w-full flex items-center justify-between">
        <div
          className="flex items-center w-full py-1 px-2 rounded-[4px] relative"
          style={{ border: "1px solid #E2E2FC" }}
        >
          <button
            style={{ backgroundColor: currentColor }}
            className="min-w-4 min-h-4 rounded-[4px] mr-[6px] color-picker-border"
            onClick={(e) => {
              setAnchorEl(e.currentTarget);
              openColorPicker(pickerId);
            }}
          />
          <input
            type="text"
            value={currentColor.toUpperCase()}
            onChange={handleColorInput}
            onBlur={handleColorInputBlur}
            className="h-6 text-[10px] rounded-[4px] flex-1 min-w-0 px-1 mr-1 flex items-center justify-center text-left"
          />
          <div
            className="absolute h-4 bg-[#E2E2FC]"
            style={{ left: "70%", width: "1px", top: "50%", transform: "translateY(-50%)" }}
          />
          {isEditingOpacity ? (
            <input
              ref={opacityInputRef}
              type="text"
              value={opacityInput}
              onChange={(e) => {
                const value = e.target.value.replace("%", "").trim();
                if (value === "" || (!isNaN(Number(value)) && Number(value) >= 0 && Number(value) <= 100)) {
                  setOpacityInput(value);
                }
              }}
              onBlur={() => {
                const opacityValue = opacityInput === "" ? 100 : Math.min(100, Math.max(0, Number(opacityInput)));
                const newColor = setOpacityPercentage(currentColor, opacityValue);
                setCurrentColor(newColor);
                setOpacity(`${opacityValue}`);
                updateBackground(newColor);
                setIsEditingOpacity(false);
                setOpacityInput("");
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") e.currentTarget.blur();
                else if (e.key === "Escape") {
                  setIsEditingOpacity(false);
                  setOpacityInput("");
                }
              }}
              className="ml-auto text-[10px] text-[#2E3860] pr-1 w-10 text-right bg-transparent border-none outline-none focus:outline-none"
              autoFocus
            />
          ) : (
            <span
              className="ml-auto text-[10px] text-[#2E3860] pr-1 cursor-pointer hover:opacity-70"
              onClick={() => {
                setOpacityInput(opacity);
                setIsEditingOpacity(true);
                setTimeout(() => {
                  opacityInputRef.current?.focus();
                  opacityInputRef.current?.select();
                }, 0);
              }}
            >
              {opacity}%
            </span>
          )}
        </div>
        <Popover
          id={`bg-color-picker-${side}`}
          open={open}
          anchorEl={anchorEl}
          onClose={closePicker}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          disableAutoFocus
          disableEnforceFocus
          disableRestoreFocus
          // Canvas-pannable plumbing. `pointerEvents:none` on the
          // Popover root + `hideBackdrop` + `disableScrollLock` lets
          // tldraw receive pointer/wheel events for pan/zoom while
          // this picker is open. Outside-click closing comes from
          // `useDraggableColorPicker`.
          style={{ pointerEvents: "none" }}
          hideBackdrop
          disableScrollLock
          // Baseline `translateX(8px)` is preserved and the drag
          // offset is layered on via the `--cp-drag-x` / `--cp-drag-y`
          // CSS variables set by the hook directly on the Paper.
          PaperProps={{
            style: {
              pointerEvents: "auto",
              borderRadius: "8px",
              transform: "translate(calc(8px + var(--cp-drag-x, 0px)), var(--cp-drag-y, 0px))",
            },
          }}
          // @ts-ignore MUI v4 Popover supports ModalProps at runtime
          ModalProps={{
            keepMounted: true,
            disableAutoFocus: true,
            disableEnforceFocus: true,
            disableRestoreFocus: true,
          }}
        >
          <div
            ref={paperContentRef}
            className="p-4 rounded-[8px] w-[264px] h-[442px] overflow-y-auto"
            // alignContent centers the inner picker vertically; see
            // ColorPickerPopover for the rationale.
            style={{ cursor: isDragging ? "grabbing" : "grab", alignContent: "center" }}
            onMouseDown={(e) => {
              // The picker's free border (padding) is the drag
              // handle; clicks on the picker content fall through to
              // the editor-history mark below.
              handleBorderMouseDown(e);
              editor.markHistoryStoppingPoint(`bg-color-change-${side}`);
            }}
          >
            <CustomColorPicker
              editor={editor}
              colorProp="fill"
              shapeColor={currentColor}
              onColorChange={handleColorChange}
              selectedShape={undefined}
            />
          </div>
        </Popover>
      </div>
    </div>
  );
};

const SettingsComponent = ({ loading }: SettingsProps) => {
  const {
    campaign: { id: campaignId, channel, flyerType, flyerWeight, subtype },
    costsCalculationData: { printingCosts },
    country,
    updateCampaign,
    updateFlyerBuilder,
    bgForTemplate,
    setIsSnapMode,
    isSnapMode,
    currentFormat: currentFormatFromStore,
    setCurrentFormat,
    currentOrientations,
    setCurrentOrientations,
    measurementUnit,
    setMeasurementUnit,
  }: StoreType = useStore();
  const { format, clientId, roomId: roomIdParam } = useParams();
  // Prefer the Zustand store over the URL param: the URL only updates on
  // navigation, but a remote collaborator's format change arrives via
  // Liveblocks → useMultiplayerState → setCurrentFormat. If we read URL first
  // here, the watcher's dropdown stays pinned to the URL value forever and
  // never reflects the broadcast.
  const currentFormat = useMemo(
    () => currentFormatFromStore || format || flyerType,
    [format, currentFormatFromStore, flyerType]
  );
  const location = useLocation();
  // Get designId from URL params (roomId) or query string (designId)
  const designId = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("designId") || roomIdParam;
  }, [location.search, roomIdParam]);
  const { updateDesign } = useDesignManagement({ clientId: clientId as string });
  const history = useHistory();
  const runtimeFormats = useRuntimeFormats();
  const [currentFormatConfig, setCurrentFormatConfig] = useState<TemplateConfig>(
    getEffectiveFormat(format) as TemplateConfig
  );
  const isPublicBuilder = useMemo(
    () => location.pathname.includes(`${ROUTES.PUBLIC_FLYER_BUILDER.split("/")[1]}/`),
    [location.pathname]
  );

  // New blank design: public builder with no format, OR internal builder with newDesign flag
  // Use useState (not useMemo) so the value is captured reliably at mount time
  // and explicitly cleared only when the user selects a format.
  const [isNewDesignActive, setIsNewDesignActive] = useState(() => {
    return new URLSearchParams(window.location.search).get("newDesign") === "true";
  });
  const isNewBlankDesign = (isPublicBuilder && !format) || (isNewDesignActive && !format);
  const workflowUuid = useRef(new URLSearchParams(window.location.search).get("workflowUuid")).current;

  const editor = useEditor();

  // An explicit `orientation` in the URL is authoritative (e.g. the Quote Manager's address-box flow
  // forces ADM/EDDM postcards to landscape). Persist it ONCE, synchronously before the orientation is
  // resolved below, so it wins over any stale per-design value cached in localStorage and every
  // orientation reader (this component, useFlyerLayout, exports…) picks it up.
  const urlOrientationRef = useRef(new URLSearchParams(window.location.search).get("orientation"));
  const urlOrientationAppliedRef = useRef(false);
  if (!urlOrientationAppliedRef.current && designId && urlOrientationRef.current) {
    const normalized =
      urlOrientationRef.current.toLowerCase() === "landscape"
        ? DocumentOrientation.Landscape
        : urlOrientationRef.current.toLowerCase() === "portrait"
        ? DocumentOrientation.Portrait
        : null;
    if (normalized) {
      urlOrientationAppliedRef.current = true;
      setStoredOrientation(normalized, designId, campaignId);
    }
  }

  // Initialize orientation from localStorage synchronously on first render.
  // This prevents the flash: store starts with "" → useFlyerLayout renders landscape →
  // then useEffect sets portrait → shapes rotate. By reading localStorage during render,
  // the store is populated before useFlyerLayout runs.
  // On reload of existing design, read orientation from localStorage ONCE before first render.
  // We override the store value with a ref-based approach so useFlyerLayout gets the right value
  // on its first pass, preventing the landscape→portrait flash that rotates shapes.
  const orientationInitRef = useRef(false);
  const resolvedOrientation = useMemo(() => {
    // Empty state: no format selected yet — don't resolve any orientation
    if (isNewDesignActive && !format) return "";
    // No orientation set yet and we have a design — try to restore from localStorage
    // But only if a format is already set (not empty state)
    if (!currentOrientations && designId && format) {
      // Nothing stored in this browser: start in the format's orientation until the saved settings load (PAD-32396).
      const stored = getStoredOrientation(designId, campaignId, defaultOrientationFor(format));
      if (stored) return stored;
    }
    return currentOrientations;
  }, [currentOrientations, isNewDesignActive, format, designId, campaignId]);

  // If resolved differs from store, sync it
  if (!orientationInitRef.current && resolvedOrientation && resolvedOrientation !== currentOrientations) {
    orientationInitRef.current = true;
    // Schedule sync for after render (can't setState during render of another component)
    Promise.resolve().then(() => setCurrentOrientations(resolvedOrientation));
  }

  // Clear stale orientation when entering new blank design state
  useEffect(() => {
    if (isNewDesignActive && !format) {
      setCurrentOrientations("");
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const { upsertAddressBox, hideAddressBox, visibleType, isAddressBoxVisible } = useAddressBox();

  // Track previous address box visibility to detect changes
  const prevAddressBoxVisibleRef = useRef<boolean | undefined>(undefined);
  const isInitialMountRef = useRef(true);
  const updateTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Sync address box visibility with database
  useEffect(() => {
    // Skip on initial mount to avoid unnecessary API calls
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      // Check actual shape existence on mount
      const addressBox = editor.getShape("shape:addressBox" as TLShapeId);
      const actualVisibility = Boolean(addressBox && addressBox.type === "customAddressBox");
      prevAddressBoxVisibleRef.current = actualVisibility;
      return;
    }

    // Clear any pending timeout
    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current);
    }

    // Use a small delay to ensure the shape has been created/removed in the editor
    updateTimeoutRef.current = setTimeout(() => {
      // Verify actual shape existence in editor, not just state
      const addressBox = editor.getShape("shape:addressBox" as TLShapeId);
      const actualVisibility = Boolean(addressBox && addressBox.type === "customAddressBox");

      // Only update if visibility actually changed and we have a designId
      if (prevAddressBoxVisibleRef.current !== actualVisibility && designId) {
        // Ensure we always pass an explicit boolean value
        const addressBoxValue = Boolean(actualVisibility);
        updateDesign({
          designId: Number(designId),
          design: {
            addressBox: addressBoxValue,
          },
        })
          .then(() => {
            console.log("[AddressBox Sync] Successfully updated addressBox to:", addressBoxValue);
          })
          .catch((error) => {
            console.error("Error updating address box in database:", error);
          });
        prevAddressBoxVisibleRef.current = actualVisibility;
      }
    }, 100); // Small delay to ensure shape creation/removal is complete

    return () => {
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    };
  }, [isAddressBoxVisible, designId, updateDesign, editor]);

  // Auto-add addressbox for new/empty designs with USPS format
  useEffect(() => {
    if (!editor || !currentFormat || !designId) {
      return;
    }

    // Use sessionStorage to persist check across component unmounts (auto-cleanup on session end)
    const storageKey = `addressbox_empty_check_${designId}`;
    const hasChecked = sessionStorage.getItem(storageKey);

    // Skip if we already checked this design
    if (hasChecked === "true") {
      return;
    }

    // Mark as checked
    sessionStorage.setItem(storageKey, "true");

    const checkAndAddAddressBox = () => {
      // Get all shapes in the canvas
      const allShapes = editor.getCurrentPageShapes();

      // Filter out system shapes (format shapes, borders, etc)
      const userShapes = allShapes.filter((shape: any) => {
        const shapeId = shape.id as string;
        // Exclude format-related shapes
        return (
          !shapeId.includes("front") &&
          !shapeId.includes("back") &&
          !shapeId.includes("Border") &&
          !shapeId.includes("label") &&
          !shapeId.includes("line")
        );
      });

      // Check if addressbox already exists
      const addressBox = editor.getShape("shape:addressBox" as TLShapeId);
      const hasAddressBox = Boolean(addressBox && addressBox.type === "customAddressBox");

      // If canvas is empty (no user shapes) and no addressbox, and format is USPS, add addressbox
      if (userShapes.length === 0 && !hasAddressBox) {
        if (isADMFormat(currentFormat)) {
          upsertAddressBox("ADM");
        } else if (isEDDMFormat(currentFormat)) {
          upsertAddressBox("EDDM");
        }
      }
    };

    // Try immediately first
    checkAndAddAddressBox();

    // Also listen for the first shape being created/loaded as a signal canvas is ready
    const unsubscribe = editor.sideEffects.registerAfterCreateHandler("shape", () => {
      checkAndAddAddressBox();
      // Unsubscribe after first check to avoid multiple triggers
      unsubscribe();
    });

    return () => {
      unsubscribe();
    };
  }, [editor, currentFormat, designId, upsertAddressBox]);

  // Auto-add addressbox when coming from Design Gallery with flag
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const shouldAutoAdd = params.get("autoAddAddressBox");

    if (shouldAutoAdd === "true" && editor && currentFormat && designId) {
      if (isADMFormat(currentFormat)) {
        upsertAddressBox("ADM");
      } else if (isEDDMFormat(currentFormat)) {
        upsertAddressBox("EDDM");
      }

      // Explicitly update the database
      setTimeout(() => {
        updateDesign({
          designId: Number(designId),
          design: { addressBox: true },
        });
      }, 200);

      // Clean up the autoAddAddressBox flag after adding
      // The addressBox will be preserved during PDF import (PDFImport filters it out)
      const currentParams = new URLSearchParams(window.location.search);
      if (currentParams.get("autoAddAddressBox")) {
        currentParams.delete("autoAddAddressBox");
        const newSearch = currentParams.toString();
        const newUrl = `${window.location.pathname}${newSearch ? `?${newSearch}` : ""}`;
        history.replace(newUrl);
      }
    }
  }, [location.search, editor, currentFormat, designId, history, upsertAddressBox, updateDesign]);

  // Handle ADM address box toggle
  const handleAdmAddressBoxToggle = useCallback(() => {
    if (visibleType === "ADM") {
      // Hide ADM address box
      hideAddressBox("ADM");
    } else {
      // Show/switch to ADM address box (upsertAddressBox handles type switching)
      upsertAddressBox("ADM");
    }
  }, [visibleType, hideAddressBox, upsertAddressBox]);

  // Handle EDDM address box toggle
  const handleEddmAddressBoxToggle = useCallback(() => {
    if (visibleType === "EDDM") {
      // Hide EDDM address box
      hideAddressBox("EDDM");
    } else {
      // Show/switch to EDDM address box (upsertAddressBox handles type switching)
      upsertAddressBox("EDDM");
    }
  }, [visibleType, hideAddressBox, upsertAddressBox]);

  if (bgForTemplate) {
    useUpdateAssets(editor, bgForTemplate);
  }

  const app = useTldrawApp();

  const handleOrientationChange = (orientation: string) => {
    setCurrentOrientations(orientation);
    setStoredOrientation(orientation, designId, campaignId);
    // Notify tour that orientation was selected
    document.body.dispatchEvent(new CustomEvent("tour:orientation-selected"));
  };

  const { recalculateCampaignCost } = useCalculating();

  const { countryCode: countryCodeParam, roomId } = useParams();
  const countryCode = country.code || countryCodeParam;
  const handleChangeFlyerType = async (newFlyerType: PossibleFlyerType) => {
    // Clear new design state so placeholders and empty state stop showing
    setIsNewDesignActive(false);

    // Notify tour that a format was selected
    document.body.dispatchEvent(new CustomEvent("tour:format-selected"));

    // Set orientation FIRST (before format) so useFlyerLayout reads the correct value
    // from localStorage when it re-runs after format change.
    // ADM/EDDM always default to Landscape.
    if (isEDDMFormat(newFlyerType) || isADMFormat(newFlyerType)) {
      setStoredOrientation(DocumentOrientation.Landscape, designId, campaignId);
      setCurrentOrientations(DocumentOrientation.Landscape);
    } else if (!currentOrientations) {
      const defaultOri = DocumentOrientation.Portrait;
      setStoredOrientation(defaultOri, designId, campaignId);
      setCurrentOrientations(defaultOri);
    }

    const previousFormat = currentFormat;
    const wasNonCompliant = !isADMFormat(previousFormat) && !isEDDMFormat(previousFormat);
    const formatChanged = previousFormat !== newFlyerType; // Detect if format actually changed

    // Auto-activate/switch addressbox based on format
    if (isADMFormat(newFlyerType)) {
      // Add delay if format is changing to let backBorderDashLine render
      if (wasNonCompliant) {
        // Switching from non-compliant: longer delay for complete canvas redraw
        setTimeout(() => {
          upsertAddressBox("ADM");
        }, 350);
      } else if (formatChanged) {
        // Switching between USPS formats: shorter delay for canvas adjustment
        setTimeout(() => {
          upsertAddressBox("ADM");
        }, 200);
      } else {
        // Same format, just toggling addressbox: no delay needed
        upsertAddressBox("ADM");
      }
    } else if (isEDDMFormat(newFlyerType)) {
      // Add delay if format is changing to let backBorderDashLine render
      if (wasNonCompliant) {
        // Switching from non-compliant: longer delay for complete canvas redraw
        setTimeout(() => {
          upsertAddressBox("EDDM");
        }, 350);
      } else if (formatChanged) {
        // Switching between USPS formats: shorter delay for canvas adjustment
        setTimeout(() => {
          upsertAddressBox("EDDM");
        }, 200);
      } else {
        // Same format, just toggling addressbox: no delay needed
        upsertAddressBox("EDDM");
      }
    } else {
      // Non-compliant format: hide any visible addressbox
      if (visibleType) {
        hideAddressBox(visibleType);
      }
    }

    updateCampaign({ flyerType: newFlyerType });
    setCurrentFormat(newFlyerType);
    const selectedFormat = getEffectiveFormat(newFlyerType);

    if (selectedFormat) {
      setCurrentFormatConfig(selectedFormat);
      let newPath: string | null = null;
      if (countryCode && roomId) {
        newPath = generatePath(ROUTES.PUBLIC_FLYER_BUILDER, {
          countryCode,
          roomId,
          format: selectedFormat.format,
        });
      } else if (campaignId && clientId) {
        // Update URL for regular campaign context
        newPath = generatePath(ROUTES.FLYER_BUILDER, {
          campaignId,
          clientId,
          format: selectedFormat.format,
        });
      }

      if (newPath) {
        // Preserve existing query params (e.g. designId) but remove newDesign flag
        const params = new URLSearchParams(window.location.search);
        params.delete("newDesign");
        const search = params.toString();
        history.push(search ? `${newPath}?${search}` : newPath);
      }
    }

    // Adjust camera to fit the new format after a short delay to ensure format change is complete
    setTimeout(() => {
      try {
        editor.zoomToFit({
          animation: { duration: 200 },
        });

        // Get the center of the page after format change
        const pageBounds = editor.getCurrentPageBounds();
        if (pageBounds) {
          const centerX = (pageBounds.minX + pageBounds.maxX) / 2;
          const centerY = (pageBounds.minY + pageBounds.maxY) / 2;
          editor.centerOnPoint({ x: centerX, y: centerY });
        }
      } catch (error) {
        console.warn("Could not adjust camera after format change:", error);
      }
    }, 200);

    if (designId) {
      updateDesign({
        designId: Number(designId),
        design: {
          format: newFlyerType,
        },
      });
    } else {
      await updateCampaignExtraData({
        campaignId,
        flyerType: newFlyerType,
      });
    }
  };

  const recalculateAddressBox = (isAddressBoxVisible: boolean) => {
    if (isAddressBoxVisible && visibleType) {
      // Recalculate the currently visible address box type
      upsertAddressBox(visibleType);
    } else {
      // Hide the currently visible address box type
      if (visibleType) {
        hideAddressBox(visibleType);
      }
    }
  };

  const getDisplaySize = (format: TemplateConfig, useAlternateUnit = false) => {
    const isInch = displayInInch(countryCode, measurementUnit);
    const unit = useAlternateUnit ? !isInch : isInch;
    const isCompliant = format.format ? isUSPSCompliant(format.format, countryCode) : false;

    // For USPS compliant formats (ADM/EDDM), display as Height x Width (USPS standard)
    // For other formats (tearaway, door hanger, etc), display as Width x Height (standard)
    if (isCompliant) {
      return getFlyerSizeDisplay(format.heightInMm, format.widthInMm, unit);
    }

    return getFlyerSizeDisplay(format.widthInMm, format.heightInMm, unit);
  };

  const isInch = displayInInch(countryCode, measurementUnit);

  const flyerTypeOptions = useMemo(() => {
    const catalogueCodes = runtimeFormats ? runtimeFormats.map((runtime) => runtime.format as string) : null;
    const sourceFormats: [string, TemplateConfig][] = runtimeFormats
      ? runtimeFormats.map((runtime) => [runtime.format as string, runtime])
      : Object.entries(FORMATS);
    return sourceFormats
      .filter(([key]) => {
        const allowedFormats = getAllowedFormats({
          channel,
          subtype,
          countryCode,
          printingCosts,
          flyerWeight,
          isPublicBuilder,
          workflowUuid,
          catalogueCodes,
        });
        return allowedFormats.includes(key);
      })
      .map(([, format]) => {
        const isCompliant = format.format ? isUSPSCompliant(format.format, countryCode) : false;
        const [w, h] = isCompliant ? [format.heightInMm, format.widthInMm] : [format.widthInMm, format.heightInMm];
        // Size in the selected unit (shown in dropdown label)
        const sizeInSelectedUnit = getFlyerSizeDisplay(w, h, isInch);
        // Size in the opposite unit (shown in compliant/helper line)
        const sizeInAltUnit = getFlyerSizeDisplay(w, h, !isInch);

        // Use only the base name (e.g. "Postcard") when the format name includes dimensions after ":", so we show "Postcard: 4" x 6"" or "Postcard: 101.6 x 152.4 mm" once, not duplicated
        const label = format.name.includes(":") ? format.name.split(":")[0].trim() : format.name;

        const category = POSTCARD_FORMATS.has(format.format || "")
          ? "ADM"
          : LARGE_FAMILY_FORMATS.has(format.format || "")
          ? "EDDM"
          : runtimeFormats && (subtype === CAMPAIGN_SUBTYPES_ENUM.ADM || subtype === CAMPAIGN_SUBTYPES_ENUM.EDDM)
          ? subtype
          : "standard";

        return {
          value: format.format,
          label,
          category,
          displaySize: sizeInSelectedUnit,
          helperText: (
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              {isCompliant && (
                <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                  <img src={uspsLogo} alt="USPS Logo" width="16" height="10" />
                  <span style={{ fontSize: "10px", color: "#707087" }}>Compliant</span>
                </div>
              )}
              <span style={{ color: "#707087" }}>{sizeInAltUnit}</span>
            </div>
          ),
          action: async () => {
            if (format.format) {
              updateFlyerBuilder({ loading: true });
              await handleChangeFlyerType(format.format as PossibleFlyerType);
              updateFlyerBuilder({ loading: false });
            }
          },
          isChecked: currentFormat === format.format,
        };
      });
  }, [currentFormat, handleChangeFlyerType, updateFlyerBuilder, isInch, measurementUnit, countryCode, runtimeFormats]);

  const flyerWeightOptions: string[] = useMemo(
    () => getFlyerWeightOptions({ flyerType, printingCosts }),
    [flyerType, printingCosts]
  );

  const getOrientationLabel = (rawOrientation: string, defaultOrientation?: string) => {
    const isPortrait = rawOrientation === DocumentOrientation.Portrait;
    const orientationLabel: string = rawOrientation;

    // No label inversion needed — dimensions are now correctly swapped based on
    // natural format orientation in useFlyerLayout, simpleHtmlExport, and formatDimensions.
    // Portrait always means width < height, Landscape always means width > height.

    const portraitSvg = (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        style={{ marginRight: "8px", flexShrink: 0 }}
      >
        <path
          d="M6 6C6 5.46957 6.21071 4.96086 6.58579 4.58579C6.96086 4.21071 7.46957 4 8 4H16C16.5304 4 17.0391 4.21071 17.4142 4.58579C17.7893 4.96086 18 5.46957 18 6V18C18 18.5304 17.7893 19.0391 17.4142 19.4142C17.0391 19.7893 16.5304 20 16 20H8C7.46957 20 6.96086 19.7893 6.58579 19.4142C6.21071 19.0391 6 18.5304 6 18V6Z"
          stroke="#2E3860"
          strokeWidth="1.25"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M10.1846 14.2708L11.9997 16.0859M11.9997 16.0859L13.8149 14.2708M11.9997 16.0859V7.91776M10.1846 9.73291L11.9997 7.91776M11.9997 7.91776L13.8149 9.73291"
          stroke="#2E3860"
          strokeWidth="1.25"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
    const landscapeSvg = (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        style={{ marginRight: "8px", flexShrink: 0 }}
      >
        <path
          d="M4 8C4 7.46957 4.21071 6.96086 4.58579 6.58579C4.96086 6.21071 5.46957 6 6 6H18C18.5304 6 19.0391 6.21071 19.4142 6.58579C19.7893 6.96086 20 7.46957 20 8V16C20 16.5304 19.7893 17.0391 19.4142 17.4142C19.0391 17.7893 18.5304 18 18 18H6C5.46957 18 4.96086 17.7893 4.58579 17.4142C4.21071 17.0391 4 16.5304 4 16V8Z"
          stroke="#2E3860"
          strokeWidth="1.25"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M9.73117 10.1836L7.91602 11.9987M7.91602 11.9987L9.73117 13.8139M7.91602 11.9987H16.0842M14.269 10.1836L16.0842 11.9987M16.0842 11.9987L14.269 13.8139"
          stroke="#2E3860"
          strokeWidth="1.25"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );

    return (
      <div style={{ display: "flex", alignItems: "center", minHeight: 0, overflow: "hidden" }}>
        {isPortrait ? portraitSvg : landscapeSvg}
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{orientationLabel}</span>
      </div>
    );
  };

  const orientationArr = useMemo(() => {
    const arr = [
      {
        label: getOrientationLabel(DocumentOrientation.Portrait, currentFormatConfig?.defaultOrientation),
        action: () => handleOrientationChange(DocumentOrientation.Portrait),
        isChecked: currentOrientations === DocumentOrientation.Portrait,
      },
      {
        label: getOrientationLabel(DocumentOrientation.Landscape, currentFormatConfig?.defaultOrientation),
        action: () => handleOrientationChange(DocumentOrientation.Landscape),
        isChecked: currentOrientations === DocumentOrientation.Landscape,
      },
    ];
    return arr;
  }, [currentOrientations, currentFormatConfig, currentFormat]);

  const handleChangeFlyerWeight = async (newFlyerWeight: string) => {
    updateCampaign({ flyerWeight: newFlyerWeight });
    await updateCampaignExtraData({
      campaignId,
      flyerWeight: newFlyerWeight,
    });
  };

  const prevFormatRef = useRef(format);
  const prevCurrentFormatConfigRef = useRef(currentFormatConfig);
  const prevFlyerWeightOptionsRef = useRef(flyerWeightOptions);
  const prevFlyerWeightRef = useRef(flyerWeight);

  useEffect(() => {
    if (prevFormatRef.current !== format) {
      setCurrentFormat(format as PossibleFlyerType);
      prevFormatRef.current = format;
    }
  }, [format, setCurrentFormat]);

  useEffect(() => {
    // Skip orientation sync when there's no format config (new blank design before format selection)
    if (!currentFormatConfig) return;

    const formatChanged = prevCurrentFormatConfigRef.current?.format !== currentFormatConfig?.format;

    if (formatChanged || !currentOrientations) {
      if (formatChanged) {
        const isFirstFormatSelection = isNewBlankDesign && !prevCurrentFormatConfigRef.current;
        if (isFirstFormatSelection && currentOrientations) {
          // User already picked orientation on a new blank design — preserve their choice.
          // No inversion needed: dimensions are now correctly swapped based on
          // natural format orientation in useFlyerLayout and simpleHtmlExport.
          setCurrentOrientations(currentOrientations);
          setStoredOrientation(currentOrientations, designId, campaignId);
        } else if (!prevCurrentFormatConfigRef.current) {
          // First render of an existing design (reload) — use stored orientation, don't override
          const storedOrientation = getStoredOrientation(designId, campaignId, currentFormatConfig.defaultOrientation);
          setCurrentOrientations(storedOrientation);
        } else {
          // User actively changed format — ADM/EDDM always default to Landscape
          const newFormat = currentFormatConfig.format;
          const defaultOri =
            isADMFormat(newFormat || "") || isEDDMFormat(newFormat || "")
              ? DocumentOrientation.Landscape
              : currentFormatConfig.defaultOrientation;
          setCurrentOrientations(defaultOri);
          setStoredOrientation(defaultOri, designId, campaignId);
        }
      } else {
        // Only use stored orientation if format hasn't changed
        const storedOrientation = getStoredOrientation(designId, campaignId, currentFormatConfig.defaultOrientation);
        if (storedOrientation) {
          setCurrentOrientations(storedOrientation);
        } else {
          setCurrentOrientations(currentFormatConfig.defaultOrientation);
        }
      }
      prevCurrentFormatConfigRef.current = currentFormatConfig;
    }
  }, [currentFormatConfig, setCurrentOrientations, designId, campaignId]);

  useEffect(() => {
    if (prevFlyerWeightOptionsRef.current !== flyerWeightOptions || prevFlyerWeightRef.current !== flyerWeight) {
      if (flyerWeightOptions.includes(flyerWeight)) {
        recalculateCampaignCost();
      } else if (flyerWeightOptions.length) {
        handleChangeFlyerWeight(flyerWeightOptions[0]);
      }
      prevFlyerWeightOptionsRef.current = flyerWeightOptions;
      prevFlyerWeightRef.current = flyerWeight;
    }
  }, [flyerWeight, flyerWeightOptions, recalculateCampaignCost, handleChangeFlyerWeight]);

  useEffect(() => {
    // Skip URL sync for new blank designs — let the empty state stay until user selects a format
    if (isNewBlankDesign) return;
    if (currentFormat && prevFormatRef.current !== currentFormat && clientId) {
      setCurrentFormat(currentFormat);
      const newPath = generatePath(ROUTES.FLYER_BUILDER, { campaignId, clientId, format: currentFormat });
      const params = new URLSearchParams(window.location.search);
      params.delete("newDesign");
      const search = params.toString();
      history.replace(search ? `${newPath}?${search}` : newPath);
      prevFormatRef.current = currentFormat;
    }
  }, [currentFormat, campaignId, clientId, history, setCurrentFormat, isNewBlankDesign]);

  useEffect(() => {
    if (!visibleType) {
      return;
    }

    if (visibleType === "EDDM" && (isPostcardFormat(currentFormat) || isDoorHangerOrTearaway(currentFormat))) {
      hideAddressBox("EDDM");
    } else if (visibleType === "ADM" && (isLargeFamilyFormat(currentFormat) || isDoorHangerOrTearaway(currentFormat))) {
      hideAddressBox("ADM");
    }
  }, [currentFormat, visibleType, hideAddressBox]);

  useEffect(() => {
    // Initialize measurement unit based on user locale if not already set
    if (!measurementUnit || measurementUnit === "") {
      const storedUnit = localStorage.getItem("measurementUnit");
      if (storedUnit && (storedUnit === "inches" || storedUnit === "mm")) {
        setMeasurementUnit(storedUnit);
      } else {
        // Use browser locale detection to determine default measurement unit
        const defaultUnit = detectMeasurementSystemFromLocale();
        setMeasurementUnit(defaultUnit);
        localStorage.setItem("measurementUnit", defaultUnit);
      }
    }
  }, [measurementUnit, setMeasurementUnit]);

  const currentOpt = flyerTypeOptions.find((o) => o.value === currentFormat);
  const displayFlyerValue = currentOpt
    ? `${currentOpt.label}: ${currentOpt.displaySize}`
    : currentFormat || "Select format";

  // DISABLED: Auto-centering camera on flyerType change to prevent unwanted centering on save
  // useEffect(() => {
  //   editor.zoomToFit({
  //     animation: { duration: 0 },
  //   });
  //   const currentZoom = editor.getZoomLevel() / 1.1;
  //   editor.setCamera({ x: 0, y: 0, z: currentZoom });

  //   // Get the center of the page
  //   const pageBounds = editor.getCurrentPageBounds();
  //   if (pageBounds) {
  //     const centerX = (pageBounds.minX + pageBounds.maxX) / 2;
  //     const centerY = (pageBounds.minY + pageBounds.maxY) / 2;
  //     editor.centerOnPoint({ x: centerX, y: centerY });
  //   }
  // }, [flyerType]);

  const isDisabledOriaentation = flyerType === DocumentFormats.TEARAWAY || flyerType === DocumentFormats.DOOR_HANGER;
  // For new blank designs, check if a format has been actively selected yet
  const hasFormatSelected = isNewBlankDesign ? false : Boolean(currentFormat);

  const handleSnapMode = () => {
    editor.user.updateUserPreferences({ isSnapMode: !isSnapMode });
    setIsSnapMode(!isSnapMode);
  };

  const handleUnitChange = useCallback(
    (newUnit: string) => {
      setMeasurementUnit(newUnit);
      localStorage.setItem("measurementUnit", newUnit);
      document.body.dispatchEvent(new CustomEvent("tour:unit-selected"));
    },
    [setMeasurementUnit]
  );

  const displayUnitValue = measurementUnit === "inches" ? "Inches" : "Mm";

  const truncateText = (text: string | undefined, maxLength: number): string => {
    if (!text || typeof text !== "string") return "";
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  const displayOrientationValue =
    currentOrientations || getStoredOrientation(designId, campaignId, currentFormatConfig?.defaultOrientation);
  // Helper function to check if current format should show address box (ADM or EDDM)
  const shouldShowAddressBox = useMemo(() => {
    // Show address box for ADM campaigns
    if (subtype === CAMPAIGN_SUBTYPES_ENUM.ADM) {
      return isADMFormat(currentFormat) || isADMFormat(format);
    }

    // Show address box for EDDM campaigns
    if (subtype === CAMPAIGN_SUBTYPES_ENUM.EDDM) {
      return isEDDMFormat(currentFormat) || isEDDMFormat(format);
    }

    return isPublicBuilder;
  }, [subtype, channel, currentFormat, format, isPublicBuilder]);

  const classes = useStyles();
  const [returnAddressAddress1, setReturnAddressAddress1] = useState("");
  const [returnAddressAddress2, setReturnAddressAddress2] = useState("");
  const [returnAddressCity, setReturnAddressCity] = useState("");
  const [returnAddressState, setReturnAddressState] = useState("");
  const [returnAddressZip, setReturnAddressZip] = useState("");
  const [isReturnAddressExpanded, setIsReturnAddressExpanded] = useState(true);
  const [showReturnAddressFields, setShowReturnAddressFields] = useState(false);
  // Whether the ADM address box prints the return-address block. Off keeps the
  // {{return_address}} placeholder out of the export so the PDF omits it.
  const [includeReturnAddress, setIncludeReturnAddress] = useState(true);
  const [addressBoxClickWarning, setAddressBoxClickWarning] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string | null>>({});
  const [savedAddresses, setSavedAddresses] = useState<
    { id: number; address: string; city: string; state: string; zipcode: string }[]
  >([]);
  const [selectedSavedAddressId, setSelectedSavedAddressId] = useState("");
  const [isReturnAddressSaving, setIsReturnAddressSaving] = useState(false);
  const [returnAddressError, setReturnAddressError] = useState<string | null>(null);
  const [isReturnAddressShaking, setIsReturnAddressShaking] = useState(false);
  const fieldDebounceRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  useEffect(() => {
    const handleWarning = () => setAddressBoxClickWarning(true);
    window.addEventListener("showAddressBoxWarning", handleWarning);
    const handleExpand = () => setIsReturnAddressExpanded(true);
    window.addEventListener("expandAddressForm", handleExpand);
    return () => {
      window.removeEventListener("showAddressBoxWarning", handleWarning);
      window.removeEventListener("expandAddressForm", handleExpand);
    };
  }, []);

  const hasSyncedAddressRef = useRef(false);
  const lastAppliedReturnAddressRef = useRef("");

  // Reset sync flag when the campaign changes so a fresh fetch is triggered
  useEffect(() => {
    hasSyncedAddressRef.current = false;
  }, [campaignId]);

  // Fetch all saved return addresses on mount for dropdown
  useEffect(() => {
    const fetchSavedAddresses = async () => {
      try {
        const response = await apiClient.getAllReturnAddresses();
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.returnAddresses) {
            setSavedAddresses(data.returnAddresses);
          }
        }
      } catch (error) {
        console.error("Error fetching saved return addresses:", error);
      }
    };

    fetchSavedAddresses();
  }, []);

  // Listen for address box warning and expand events
  useEffect(() => {
    const handleWarning = () => setAddressBoxClickWarning(true);
    window.addEventListener("showAddressBoxWarning", handleWarning);
    const handleExpand = () => setIsReturnAddressExpanded(true);
    window.addEventListener("expandAddressForm", handleExpand);
    return () => {
      window.removeEventListener("showAddressBoxWarning", handleWarning);
      window.removeEventListener("expandAddressForm", handleExpand);
    };
  }, []);

  // Parse stored returnAddress into 5 fields
  // Supports both formats: "Addr1, Addr2, City, ST 12345" and legacy "Addr1\nAddr2\nCity, ST 12345"
  const parseReturnAddressToFields = useCallback((text: string) => {
    const t = (text || "").trim();
    if (!t || t.includes("{{return_address}}")) {
      return { address1: "", address2: "", city: "", state: "", zip: "" };
    }
    // Legacy newline format: convert to single line first
    const singleLine = t.includes("\n")
      ? t
          .split(/\n/)
          .map((s) => s.trim())
          .filter(Boolean)
          .join(", ")
      : t;
    // Match "State ZIP" at the end: ", ST 12345" or ", ST 12345-1234"
    const endMatch = singleLine.match(/,\s*([A-Za-z]{2})\s+(\d{5}(-\d{4})?)\s*$/);
    if (!endMatch) {
      return { address1: singleLine, address2: "", city: "", state: "", zip: "" };
    }
    const state = endMatch[1].toUpperCase();
    const zip = endMatch[2];
    // Everything before ", ST ZIP"
    const before = singleLine.slice(0, endMatch.index!).trim();
    const parts = before
      .split(",")
      .map((p) => p.trim())
      .filter(Boolean);
    if (parts.length === 0) {
      return { address1: "", address2: "", city: "", state, zip };
    }
    if (parts.length === 1) {
      return { address1: parts[0], address2: "", city: "", state, zip };
    }
    if (parts.length === 2) {
      return { address1: parts[0], address2: "", city: parts[1], state, zip };
    }
    // 3+: first is address1, last is city, middle joined is address2
    return { address1: parts[0], address2: parts.slice(1, -1).join(", "), city: parts[parts.length - 1], state, zip };
  }, []);

  // Build single-line string for shape: "Address1, Address2, City, ST 12345"
  const buildReturnAddressString = useCallback(
    (address1: string, address2: string, city: string, state: string, zip: string) => {
      const a1 = address1.trim();
      const a2 = address2.trim();
      const c = city.trim();
      const st = state.trim();
      const z = zip.trim();
      const stateZip = st && z ? `${st} ${z}` : "";
      const parts = [a1, a2, c, stateZip].filter(Boolean);
      return parts.join(", ") || "{{return_address}}";
    },
    []
  );

  // Effect 1: populate sidebar from the shape's return address (persisted in tldraw store)
  useEffect(() => {
    if (visibleType !== "ADM" || loading) {
      return;
    }

    const addressBoxShape = editor.getShape("shape:addressBox" as TLShapeId) as CustomAddressBoxShape | null;
    if (!addressBoxShape?.props) {
      return;
    }

    const currentText = addressBoxShape.props.returnAddress || "";
    // Always sync from shape to fields so that reopening the sidebar rehydrates the inputs
    let { address1, address2, city, state, zip } = parseReturnAddressToFields(currentText);

    // The shape format loses partial city/state/zip (requires all three to store any).
    // If parsing returned empty but the shape has some data, restore from sessionStorage.
    const hasPartialShapeData = currentText && !currentText.includes("{{return_address}}");
    if (!address1 && !city && !state && !zip && hasPartialShapeData) {
      const storageKey = `address_fields_${designId || campaignId}`;
      const saved = sessionStorage.getItem(storageKey);
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          address1 = parsed.a1 || "";
          address2 = parsed.a2 || "";
          city = parsed.city || "";
          state = parsed.state || "";
          zip = parsed.zip || "";
        } catch (_e) {
          // Ignore corrupted sessionStorage data
        }
      }
    }

    // Try to match against saved addresses so we reuse the ID instead of creating duplicates
    if (savedAddresses.length > 0) {
      const a1 = address1.trim().toLowerCase();
      const c = city.trim().toLowerCase();
      const st = state.trim().toUpperCase();
      const z = zip.trim();
      const match = savedAddresses.find(
        (ra) =>
          ra.address.toLowerCase() === a1 &&
          ra.city.toLowerCase() === c &&
          ra.state.toUpperCase() === st &&
          ra.zipcode === z
      );
      if (match) {
        setSelectedSavedAddressId(String(match.id));
      }
    }

    hasSyncedAddressRef.current = true;

    setReturnAddressAddress1(address1);
    setReturnAddressAddress2(address2);
    setReturnAddressCity(city);
    setReturnAddressState(state);
    setReturnAddressZip(zip);
    lastAppliedReturnAddressRef.current = currentText;

    // Show the 5 fields if the shape already has address data (e.g. reopening panel)
    if (address1.trim() || city.trim() || state.trim() || zip.trim()) {
      setShowReturnAddressFields(true);
    }

    // Collapse the form if required fields are filled; expand if not
    const requiredFilled = !!(address1.trim() && city.trim() && state.trim() && zip.trim());
    setFieldErrors({});
    setAddressBoxClickWarning(false);
    setIsReturnAddressExpanded(!requiredFilled);
  }, [visibleType, editor, parseReturnAddressToFields, savedAddresses, buildReturnAddressString, loading]);

  // Effect 1b: when savedAddresses loads, try to match current fields against it
  useEffect(() => {
    if (savedAddresses.length === 0) return;
    const a1 = returnAddressAddress1.trim().toLowerCase();
    const c = returnAddressCity.trim().toLowerCase();
    const st = returnAddressState.trim().toUpperCase();
    const z = returnAddressZip.trim();
    if (!a1 && !c && !st && !z) return;
    const match = savedAddresses.find(
      (ra) =>
        ra.address.toLowerCase() === a1 &&
        ra.city.toLowerCase() === c &&
        ra.state.toUpperCase() === st &&
        ra.zipcode === z
    );
    setSelectedSavedAddressId(match ? String(match.id) : "");
  }, [savedAddresses]); // eslint-disable-line react-hooks/exhaustive-deps

  // Effect 2: if shape has no return address, fall back to the campaign's linked address
  useEffect(() => {
    if (visibleType !== "ADM" || !campaignId || savedAddresses.length === 0) {
      return;
    }
    if (hasSyncedAddressRef.current) {
      return;
    }
    hasSyncedAddressRef.current = true;

    const fetchLinkedAddress = async () => {
      try {
        const campaignData = await getCampaign(campaignId, clientId);
        const linkedId = campaignData?.returnAddressId;
        if (!linkedId) return;

        const match = savedAddresses.find(
          (a: { id: number; address: string; city: string; state: string; zipcode: string }) =>
            Number(a.id) === Number(linkedId)
        );
        if (!match) return;

        // Populate the 5 fields from the matched saved address
        setReturnAddressAddress1(match.address);
        setReturnAddressAddress2("");
        setReturnAddressCity(match.city);
        setReturnAddressState(match.state);
        setReturnAddressZip(match.zipcode);
        setShowReturnAddressFields(true);
        setSelectedSavedAddressId(String(match.id));
        setIsReturnAddressExpanded(false);

        // Persist in shape so re-entries don't need another network call
        const displayText = buildReturnAddressString(match.address, "", match.city, match.state, match.zipcode);
        const addressBoxShape = editor.getShape("shape:addressBox" as TLShapeId) as CustomAddressBoxShape | null;
        if (addressBoxShape?.props) {
          editor.run(
            () => {
              editor.updateShape({
                id: "shape:addressBox" as TLShapeId,
                type: "customAddressBox",
                props: {
                  ...addressBoxShape.props,
                  returnAddress: displayText,
                  displayReturnAddress: displayText,
                },
              });
            },
            { ignoreShapeLock: true }
          );
        }
      } catch (error) {
        // Non-critical — sidebar stays empty, user can still type an address
        console.error("[Settings]: Failed to fetch linked return address for campaign", error);
      }
    };

    fetchLinkedAddress();
  }, [visibleType, campaignId, clientId, savedAddresses, editor, buildReturnAddressString]);

  // Apply current return address fields to the address box shape (live as user types)
  const applyReturnAddressToShape = useCallback(
    (address1: string, address2: string, city: string, state: string, zip: string) => {
      const addressBoxShape = editor.getShape("shape:addressBox" as TLShapeId) as CustomAddressBoxShape | null;
      if (!addressBoxShape?.props) return;
      const str = buildReturnAddressString(
        address1.trim(),
        address2.trim(),
        city.trim(),
        state.trim().toUpperCase(),
        zip.trim()
      );
      lastAppliedReturnAddressRef.current = str;
      editor.run(
        () => {
          editor.updateShape({
            id: "shape:addressBox" as TLShapeId,
            type: "customAddressBox",
            props: { ...addressBoxShape.props, returnAddress: str, displayReturnAddress: str },
          });
        },
        { ignoreShapeLock: true }
      );
      // Check if current fields still match a saved address
      const a1 = address1.trim().toLowerCase();
      const c = city.trim().toLowerCase();
      const st = state.trim().toUpperCase();
      const z = zip.trim();
      const match = savedAddresses.find(
        (ra) =>
          ra.address.toLowerCase() === a1 &&
          ra.city.toLowerCase() === c &&
          ra.state.toUpperCase() === st &&
          ra.zipcode === z
      );
      setSelectedSavedAddressId(match ? String(match.id) : "");
    },
    [editor, buildReturnAddressString, savedAddresses]
  );

  const triggerReturnAddressError = useCallback((message: string) => {
    setReturnAddressError(message);
    setIsReturnAddressShaking(true);
    setTimeout(() => setIsReturnAddressShaking(false), 500);
  }, []);

  // Keep the checkbox in sync with the loaded design's address box.
  useEffect(() => {
    if (visibleType !== "ADM") return;
    const box = editor.getShape("shape:addressBox" as TLShapeId) as CustomAddressBoxShape | null;
    if (box?.type === "customAddressBox") {
      setIncludeReturnAddress(box.props.showReturnAddress !== false);
    }
  }, [visibleType, isAddressBoxVisible, editor, designId]);

  // Toggle whether the ADM address box renders (and therefore exports) the return address.
  const handleToggleIncludeReturnAddress = useCallback(
    (next: boolean) => {
      setIncludeReturnAddress(next);
      const box = editor.getShape("shape:addressBox" as TLShapeId) as CustomAddressBoxShape | null;
      if (!box?.props) return;
      editor.run(
        () => {
          editor.updateShape({
            id: "shape:addressBox" as TLShapeId,
            type: "customAddressBox",
            props: { ...box.props, showReturnAddress: next },
          });
        },
        { ignoreShapeLock: true }
      );
    },
    [editor]
  );

  // Save the current address to backend and link to workflow
  const handleReturnAddressSave = useCallback(async () => {
    const a1 = returnAddressAddress1.trim();
    const c = returnAddressCity.trim();
    const st = returnAddressState.trim();
    const z = returnAddressZip.trim();

    if (!a1 || !c || !st || !z) {
      triggerReturnAddressError("Please fill in all required address fields.");
      return;
    }

    const structuredAddress = {
      address: a1,
      city: c,
      state: st.toUpperCase(),
      zipcode: z,
    };

    setIsReturnAddressSaving(true);
    try {
      let returnAddressId: number | undefined;

      if (selectedSavedAddressId) {
        // User picked an existing saved address — reuse it, don't create a duplicate
        returnAddressId = Number(selectedSavedAddressId);
      } else {
        // New address typed manually — save it first
        const saveResponse = await apiClient.saveReturnAddress(structuredAddress);
        if (!saveResponse.ok) {
          const data = await saveResponse.json().catch(() => ({}));
          triggerReturnAddressError(data.message || "Failed to save return address.");
          return;
        }
        const saveData = await saveResponse.json();
        returnAddressId = saveData.returnAddress?.id;
        if (returnAddressId) {
          setSelectedSavedAddressId(String(returnAddressId));
          setSavedAddresses((prev) => [...prev, { id: returnAddressId!, ...structuredAddress }]);
        }
      }

      // Link to workflow if applicable
      if (returnAddressId && workflowUuid) {
        const response = await apiClient.updateWorkflow(workflowUuid, {
          returnAddressId: Number(returnAddressId),
        });
        if (!response.ok) {
          triggerReturnAddressError("Return address saved but failed to link to workflow.");
          return;
        }
      }

      setReturnAddressError(null);
      setIsReturnAddressShaking(false);
    } catch (error) {
      triggerReturnAddressError("Failed to save return address.");
    } finally {
      setIsReturnAddressSaving(false);
    }
  }, [
    returnAddressAddress1,
    returnAddressCity,
    returnAddressState,
    returnAddressZip,
    selectedSavedAddressId,
    workflowUuid,
    triggerReturnAddressError,
  ]);

  const validateField = useCallback((fieldName: string, value: string): string | null => {
    const v = value.trim();
    switch (fieldName) {
      case "address1":
        return v.length === 0
          ? "Enter a street address"
          : !/\d/.test(v)
          ? "Include a street number (e.g. 123 Main St)"
          : null;
      case "city":
        return v.length === 0 ? "Enter a city name" : null;
      case "state":
        return v.length === 0 ? "Enter a state" : !/^[A-Za-z]{2}$/.test(v) ? "Use 2-letter state code (e.g. NY)" : null;
      case "zip":
        return v.length === 0
          ? "Enter a ZIP code"
          : !/^\d{5}(-\d{4})?$/.test(v)
          ? "Use format 12345 or 12345-1234"
          : null;
      default:
        return null;
    }
  }, []);

  const handleReturnAddressFieldChange = useCallback(
    (
        setter: React.Dispatch<React.SetStateAction<string>>,
        getNextValues: (value: string) => { a1: string; a2: string; city: string; state: string; zip: string },
        fieldName?: string
      ) =>
      (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setter(value);
        setAddressBoxClickWarning(false);
        const { a1, a2, city, state, zip } = getNextValues(value);
        applyReturnAddressToShape(a1, a2, city, state, zip);
        // If all fields are cleared, show only the search address again
        if (!a1.trim() && !a2.trim() && !city.trim() && !state.trim() && !zip.trim()) {
          setShowReturnAddressFields(false);
        }
        // Persist partial fields across tab switches (shape format loses partial city/state/zip)
        const storageKey = `address_fields_${designId || campaignId}`;
        sessionStorage.setItem(storageKey, JSON.stringify({ a1, a2, city, state, zip }));
        // Debounced field validation
        if (fieldName) {
          if (fieldDebounceRef.current[fieldName]) clearTimeout(fieldDebounceRef.current[fieldName]);
          // Clear error immediately if user is correcting
          const instant = validateField(fieldName, value);
          if (!instant) {
            setFieldErrors((prev) => ({ ...prev, [fieldName]: null }));
          } else {
            fieldDebounceRef.current[fieldName] = setTimeout(() => {
              setFieldErrors((prev) => ({ ...prev, [fieldName]: validateField(fieldName, value) }));
            }, 800);
          }
        }
      },
    [applyReturnAddressToShape, designId, campaignId, validateField]
  );

  // Helpers so each input passes current state + its new value to applyReturnAddressToShape
  const getValuesAfterAddress1 = (value: string) => ({
    a1: value,
    a2: returnAddressAddress2,
    city: returnAddressCity,
    state: returnAddressState,
    zip: returnAddressZip,
  });
  const getValuesAfterAddress2 = (value: string) => ({
    a1: returnAddressAddress1,
    a2: value,
    city: returnAddressCity,
    state: returnAddressState,
    zip: returnAddressZip,
  });
  const getValuesAfterCity = (value: string) => ({
    a1: returnAddressAddress1,
    a2: returnAddressAddress2,
    city: value,
    state: returnAddressState,
    zip: returnAddressZip,
  });
  const getValuesAfterState = (value: string) => ({
    a1: returnAddressAddress1,
    a2: returnAddressAddress2,
    city: returnAddressCity,
    state: value,
    zip: returnAddressZip,
  });
  const getValuesAfterZip = (value: string) => ({
    a1: returnAddressAddress1,
    a2: returnAddressAddress2,
    city: returnAddressCity,
    state: returnAddressState,
    zip: value,
  });

  // When user selects an address from PlaceAutocomplete, fill the 5 fields and apply to shape
  const handleReturnAddressFromPlace = useCallback(
    (fields: { address1: string; address2: string; city: string; state: string; zip: string }) => {
      setReturnAddressAddress1(fields.address1);
      setReturnAddressAddress2(fields.address2);
      setReturnAddressCity(fields.city);
      setReturnAddressState(fields.state);
      setReturnAddressZip(fields.zip);
      setShowReturnAddressFields(true);
      applyReturnAddressToShape(fields.address1, fields.address2, fields.city, fields.state, fields.zip);
      setAddressBoxClickWarning(false);
      setFieldErrors({ address1: null, city: null, state: null, zip: null });
      const storageKey = `address_fields_${designId || campaignId}`;
      sessionStorage.setItem(
        storageKey,
        JSON.stringify({
          a1: fields.address1,
          a2: fields.address2,
          city: fields.city,
          state: fields.state,
          zip: fields.zip,
        })
      );
    },
    [applyReturnAddressToShape, designId, campaignId]
  );

  // When user picks a saved address from the dropdown, populate the 5 fields
  const handleSavedAddressSelect = useCallback(
    (e: React.ChangeEvent<{ value: unknown }>) => {
      const selectedId = String(e.target.value);
      if (!selectedId) return;
      const selected = savedAddresses.find((ra) => String(ra.id) === selectedId);
      if (!selected) return;
      setSelectedSavedAddressId(selectedId);
      setReturnAddressAddress1(selected.address);
      setReturnAddressAddress2("");
      setReturnAddressCity(selected.city);
      setReturnAddressState(selected.state);
      setReturnAddressZip(selected.zipcode);
      setShowReturnAddressFields(true);
      applyReturnAddressToShape(selected.address, "", selected.city, selected.state, selected.zipcode);
      setReturnAddressError(null);
      setIsReturnAddressShaking(false);
      setFieldErrors({});
    },
    [savedAddresses, applyReturnAddressToShape]
  );

  // Disable next field until previous has content; Address 2 is the only one that can be skipped (optional)
  const canEditAddress2 = returnAddressAddress1.trim().length > 0;
  const canEditCity = returnAddressAddress1.trim().length > 0;
  const canEditState = returnAddressCity.trim().length > 0;
  const canEditZip = returnAddressState.trim().length > 0;

  const selectMenuProps = {
    PaperProps: { className: classes.selectMenuPaper },
    getContentAnchorEl: null as any,
    anchorOrigin: { vertical: "bottom" as const, horizontal: "left" as const },
    transformOrigin: { vertical: "top" as const, horizontal: "left" as const },
    disableAutoFocusItem: true,
  };

  const isGuest = !authorization.getUser?.()?.id;
  const isEmptyState = useIsBuilderEmptyState();
  const handleNewDesignClick = () => {
    removeGuestPlaceholders(editor);
    const params = new URLSearchParams(location.search);
    params.set("newDesign", "true");
    history.replace({ search: params.toString() });
    // Guests don't get the first-time tour automatically — we hold it
    // back until they opt into a real editable canvas via "+ New
    // Design". Fire it now so they land on the empty state already in
    // tour mode. The same `tour:start` event is what the in-app help
    // button uses to re-trigger the tour for any user.
    window.dispatchEvent(new CustomEvent("tour:start"));
  };

  return (
    <div className={classes.settingsScroll}>
      <h2 className={classes.sectionTitle}>Design</h2>
      {isGuest && !isEmptyState && (
        <button
          type="button"
          onClick={handleNewDesignClick}
          data-cypress-id="guest-new-design"
          style={{
            display: "flex",
            width: "100%",
            height: 39.5,
            paddingLeft: 8,
            alignItems: "center",
            gap: 8,
            borderRadius: 8,
            border: "1px solid #EE4360",
            background: "transparent",
            cursor: "pointer",
            marginTop: 16,
            marginBottom: 16,
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            style={{ width: 16, height: 16, flexShrink: 0, aspectRatio: "1/1" }}
          >
            <path
              d="M8 3.33334V12.6667M3.33334 8H12.6667"
              stroke="#EE4360"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span
            style={{
              color: "#EE4360",
              textAlign: "center",
              fontFamily: '"Inter"',
              fontSize: 13,
              fontStyle: "normal",
              fontWeight: 600,
              lineHeight: "19.5px",
            }}
          >
            New Design
          </span>
        </button>
      )}
      <div className={classes.settings_row} style={{ marginBottom: 8 }} data-tour="format">
        <label className={classes.label}>Format</label>
        <Tooltip
          title={displayFlyerValue.length > 20 && !isNewBlankDesign && currentFormat ? displayFlyerValue : ""}
          placement="top"
          arrow
          classes={{ tooltip: classes.tooltip, arrow: classes.tooltipArrow }}
        >
          <div>
            <Select
              variant="outlined"
              displayEmpty
              value={isNewBlankDesign ? "" : currentFormat || ""}
              onChange={(e) => {
                const opt = flyerTypeOptions.find((o) => o.value === e.target.value);
                if (opt?.action) opt.action();
              }}
              renderValue={() => {
                if (isNewBlankDesign || !currentFormat) {
                  return <span style={{ color: "#2E3860" }}>Select format</span>;
                }
                return truncateText(displayFlyerValue, 20);
              }}
              className={classes.selectLikeFontSize}
              MenuProps={selectMenuProps}
              IconComponent={DropdownChevron}
              data-cypress_id="flyerType"
            >
              {isPublicBuilder
                ? (() => {
                    const standardOpts = flyerTypeOptions.filter((o) => o.category === "standard");
                    const eddmOpts = flyerTypeOptions.filter((o) => o.category === "EDDM");
                    const admOpts = flyerTypeOptions.filter((o) => o.category === "ADM");

                    const items: React.ReactNode[] = [];

                    standardOpts.forEach((opt) => {
                      items.push(
                        <MenuItem key={opt.value} value={opt.value}>
                          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                            <span>
                              {opt.label}: {opt.displaySize}
                            </span>
                            {opt.helperText}
                          </div>
                        </MenuItem>
                      );
                    });

                    if (eddmOpts.length > 0) {
                      items.push(
                        <MenuItem
                          key="__eddm_header"
                          disabled
                          style={{
                            color: "#707087",
                            fontSize: "11px",
                            fontWeight: 600,
                            textTransform: "uppercase",
                            lineHeight: "32px",
                            borderTop: "1px solid #E0E0E0",
                            marginTop: "4px",
                            opacity: 1,
                            pointerEvents: "none",
                            minHeight: "auto",
                            paddingTop: "4px",
                            paddingBottom: 0,
                          }}
                        >
                          EDDM
                        </MenuItem>
                      );
                      eddmOpts.forEach((opt) => {
                        items.push(
                          <MenuItem key={opt.value} value={opt.value}>
                            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                              <span>
                                {opt.label}: {opt.displaySize}
                              </span>
                              {opt.helperText}
                            </div>
                          </MenuItem>
                        );
                      });
                    }

                    if (admOpts.length > 0) {
                      items.push(
                        <MenuItem
                          key="__adm_header"
                          disabled
                          style={{
                            color: "#707087",
                            fontSize: "11px",
                            fontWeight: 600,
                            textTransform: "uppercase",
                            lineHeight: "32px",
                            borderTop: "1px solid #E0E0E0",
                            marginTop: "4px",
                            opacity: 1,
                            pointerEvents: "none",
                            minHeight: "auto",
                            paddingTop: "4px",
                            paddingBottom: 0,
                          }}
                        >
                          ADM
                        </MenuItem>
                      );
                      admOpts.forEach((opt) => {
                        items.push(
                          <MenuItem key={opt.value} value={opt.value}>
                            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                              <span>
                                {opt.label}: {opt.displaySize}
                              </span>
                              {opt.helperText}
                            </div>
                          </MenuItem>
                        );
                      });
                    }

                    return items;
                  })()
                : flyerTypeOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                        <span>
                          {opt.label}: {opt.displaySize}
                        </span>
                        {opt.helperText}
                      </div>
                    </MenuItem>
                  ))}
            </Select>
          </div>
        </Tooltip>
      </div>
      <div className={classes.settings_row} data-tour="orientation">
        <label className={classes.label}>Orientation</label>
        <Select
          variant="outlined"
          disabled={isDisabledOriaentation}
          displayEmpty
          value={
            isNewBlankDesign && !currentOrientations
              ? ""
              : currentOrientations || currentFormatConfig?.defaultOrientation || ""
          }
          onChange={(e) => handleOrientationChange(e.target.value as string)}
          renderValue={(value: unknown) => {
            if (!value) {
              return <span style={{ color: "#2E3860" }}>Select orientation</span>;
            }
            return getOrientationLabel(String(value), currentFormatConfig?.defaultOrientation);
          }}
          className={classes.selectLikeFontSize}
          MenuProps={selectMenuProps}
          IconComponent={DropdownChevron}
          data-cypress_id="orientation"
        >
          <MenuItem value={DocumentOrientation.Portrait}>
            {getOrientationLabel(DocumentOrientation.Portrait, currentFormatConfig?.defaultOrientation)}
          </MenuItem>
          <MenuItem value={DocumentOrientation.Landscape}>
            {getOrientationLabel(DocumentOrientation.Landscape, currentFormatConfig?.defaultOrientation)}
          </MenuItem>
        </Select>
      </div>
      {shouldShowAddressBox && (
        <>
          {(subtype === CAMPAIGN_SUBTYPES_ENUM.ADM || (isPublicBuilder && isADMFormat(currentFormat))) && (
            <>
              <div
                data-tour="address-box"
                className="flex items-center gap-2 justify-start text-[16px] max-h-[28px] min-h-[28px]"
                style={{ marginBottom: visibleType !== "ADM" ? 8 : undefined }}
              >
                <div className="cursor-pointer flex items-center" onClick={handleAdmAddressBoxToggle}>
                  {visibleType === "ADM" ? <CheckedBoxIcon /> : <UncheckedBoxIcon />}
                </div>
                <div
                  className="flex items-center gap-2 flex-1"
                  style={{
                    color: "#2E3860",
                    fontFeatureSettings: "'liga' off, 'clig' off",
                    fontFamily: '"Inter", sans-serif',
                    fontSize: 12,
                    fontWeight: 400,
                    lineHeight: "20px",
                  }}
                >
                  <span
                    className={`cursor-pointer ${visibleType === "EDDM" ? "text-gray-400" : ""}`}
                    onClick={handleAdmAddressBoxToggle}
                  >
                    Display the address box
                  </span>
                  {visibleType === "ADM" && (
                    <button
                      type="button"
                      className="flex items-center justify-center p-1 cursor-pointer border-0 bg-transparent"
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsReturnAddressExpanded((prev) => !prev);
                      }}
                      aria-label={isReturnAddressExpanded ? "Collapse return address" : "Expand return address"}
                    >
                      <DropdownChevron
                        style={{
                          transform: isReturnAddressExpanded ? "rotate(180deg)" : "rotate(0deg)",
                          transition: "transform 0.2s ease",
                          position: "static",
                        }}
                      />
                    </button>
                  )}
                </div>
              </div>
              {visibleType === "ADM" && addressBoxClickWarning && (
                <div className={classes.errorTooltip} role="alert" aria-live="assertive" style={{ marginTop: 8 }}>
                  Please fill in the required address fields
                </div>
              )}
              {visibleType === "ADM" && (
                <div
                  className={`mt-2 flex flex-col gap-2.5 ${classes.returnAddressContainer}`}
                  style={{ marginBottom: isReturnAddressExpanded ? 16 : undefined }}
                  onClick={(event) => event.stopPropagation()}
                >
                  {isReturnAddressExpanded && (
                    <>
                      <div className="flex items-center gap-2 justify-start text-[16px] max-h-[28px] min-h-[28px]">
                        <div
                          className="cursor-pointer flex items-center"
                          onClick={() => handleToggleIncludeReturnAddress(!includeReturnAddress)}
                        >
                          {includeReturnAddress ? <CheckedBoxIcon /> : <UncheckedBoxIcon />}
                        </div>
                        <div
                          className="flex items-center gap-2 flex-1"
                          style={{
                            color: "#2E3860",
                            fontFeatureSettings: "'liga' off, 'clig' off",
                            fontFamily: '"Inter", sans-serif',
                            fontSize: 12,
                            fontWeight: 400,
                            lineHeight: "20px",
                          }}
                        >
                          <span
                            className="cursor-pointer"
                            onClick={() => handleToggleIncludeReturnAddress(!includeReturnAddress)}
                          >
                            Include return address
                          </span>
                        </div>
                      </div>
                      {!includeReturnAddress && (
                        <span
                          style={{
                            color: "#57578E",
                            fontFeatureSettings: "'liga' off, 'clig' off",
                            fontFamily: '"Inter", sans-serif',
                            fontSize: 11,
                            fontWeight: 400,
                            lineHeight: "14px",
                          }}
                        >
                          The return address won&apos;t be printed on the flyer.
                        </span>
                      )}
                      {includeReturnAddress && (
                        <>
                          {savedAddresses.length > 0 && (
                            <div className="flex flex-col gap-1">
                              <label className={classes.returnAddressFieldLabel}>Saved Addresses</label>
                              <Tooltip
                                title={(() => {
                                  if (!selectedSavedAddressId) return "";
                                  const match = savedAddresses.find((ra) => String(ra.id) === selectedSavedAddressId);
                                  return match
                                    ? [match.address, match.city, match.state, match.zipcode].filter(Boolean).join(", ")
                                    : "";
                                })()}
                                placement="top"
                                arrow
                                classes={{ tooltip: classes.tooltip, arrow: classes.tooltipArrow }}
                              >
                                <div>
                                  <Select
                                    variant="outlined"
                                    displayEmpty
                                    value={selectedSavedAddressId}
                                    onChange={handleSavedAddressSelect}
                                    renderValue={(value: unknown) => {
                                      if (!value) {
                                        return <span style={{ color: "#2E3860" }}>Select a saved address</span>;
                                      }
                                      const match = savedAddresses.find((ra) => String(ra.id) === String(value));
                                      if (!match) return String(value);
                                      const full = [match.address, match.city, match.state, match.zipcode]
                                        .filter(Boolean)
                                        .join(", ");
                                      return (
                                        <span
                                          style={{
                                            display: "block",
                                            maxWidth: 150,
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                          }}
                                        >
                                          {full}
                                        </span>
                                      );
                                    }}
                                    className={classes.selectLikeFontSize}
                                    MenuProps={selectMenuProps}
                                    IconComponent={DropdownChevron}
                                  >
                                    {savedAddresses.map((ra) => (
                                      <MenuItem key={ra.id} value={String(ra.id)}>
                                        {[ra.address, ra.city, ra.state, ra.zipcode].filter(Boolean).join(", ")}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </div>
                              </Tooltip>
                            </div>
                          )}
                          <div className="flex flex-col gap-1">
                            <label className={classes.returnAddressFieldLabel}>Search Address</label>
                            <Box className={`w-full ${classes.returnAddressAutocomplete}`}>
                              <PlaceAutocomplete
                                countryCode={countryCode || undefined}
                                placeholder="Enter address to search"
                                onPlaceChangedWithAddress={handleReturnAddressFromPlace}
                                iconSize="xs"
                                optionFontSize="12px"
                                popperMinWidth={270}
                              />
                            </Box>
                          </div>
                          {showReturnAddressFields && (
                            <>
                              <div className="flex flex-col gap-1">
                                <label className={classes.returnAddressFieldLabel}>Address 1</label>
                                <input
                                  type="text"
                                  className={`w-full h-[40px] rounded-lg bg-transparent px-2 py-1 text-sm placeholder:text-[#2E3860] focus:outline-none`}
                                  style={{
                                    border: "1px solid",
                                    borderColor: fieldErrors.address1 ? "#dc2626" : "#E2E2FC",
                                  }}
                                  placeholder="Street number and name"
                                  value={returnAddressAddress1}
                                  onChange={handleReturnAddressFieldChange(
                                    setReturnAddressAddress1,
                                    getValuesAfterAddress1,
                                    "address1"
                                  )}
                                />
                                {fieldErrors.address1 && (
                                  <span className={classes.fieldError}>{fieldErrors.address1}</span>
                                )}
                              </div>
                              <div className="flex flex-col gap-1">
                                <label className={classes.returnAddressFieldLabel}>Address 2 (optional)</label>
                                <input
                                  type="text"
                                  className="w-full h-[40px] rounded-lg bg-transparent px-2 py-1 text-sm placeholder:text-[#2E3860]"
                                  style={{ border: "1px solid", borderColor: "#E2E2FC" }}
                                  placeholder="Apt, suite, etc."
                                  value={returnAddressAddress2}
                                  onChange={handleReturnAddressFieldChange(
                                    setReturnAddressAddress2,
                                    getValuesAfterAddress2
                                  )}
                                />
                              </div>
                              <div className="flex flex-col gap-1">
                                <label className={classes.returnAddressFieldLabel}>City</label>
                                <input
                                  type="text"
                                  className={`w-full h-[40px] rounded-lg bg-transparent px-2 py-1 text-sm placeholder:text-[#2E3860]`}
                                  style={{ border: "1px solid", borderColor: fieldErrors.city ? "#dc2626" : "#E2E2FC" }}
                                  placeholder="City name"
                                  value={returnAddressCity}
                                  onChange={handleReturnAddressFieldChange(
                                    setReturnAddressCity,
                                    getValuesAfterCity,
                                    "city"
                                  )}
                                />
                                {fieldErrors.city && <span className={classes.fieldError}>{fieldErrors.city}</span>}
                              </div>
                              <div className="flex flex-col gap-1">
                                <label className={classes.returnAddressFieldLabel}>State</label>
                                <input
                                  type="text"
                                  className={`w-full h-[40px] rounded-lg bg-transparent px-2 py-1 text-sm placeholder:text-[#2E3860]`}
                                  style={{
                                    border: "1px solid",
                                    borderColor: fieldErrors.state ? "#dc2626" : "#E2E2FC",
                                  }}
                                  placeholder="e.g. NY"
                                  value={returnAddressState}
                                  onChange={handleReturnAddressFieldChange(
                                    setReturnAddressState,
                                    getValuesAfterState,
                                    "state"
                                  )}
                                />
                                {fieldErrors.state && <span className={classes.fieldError}>{fieldErrors.state}</span>}
                              </div>
                              <div className="flex flex-col gap-1">
                                <label className={classes.returnAddressFieldLabel}>ZIP Code</label>
                                <input
                                  type="text"
                                  className={`w-full h-[40px] rounded-lg bg-transparent px-2 py-1 text-sm placeholder:text-[#2E3860]`}
                                  style={{ border: "1px solid", borderColor: fieldErrors.zip ? "#dc2626" : "#E2E2FC" }}
                                  placeholder="e.g. 10001 or 10001-1234"
                                  value={returnAddressZip}
                                  onChange={handleReturnAddressFieldChange(
                                    setReturnAddressZip,
                                    getValuesAfterZip,
                                    "zip"
                                  )}
                                />
                                {fieldErrors.zip && <span className={classes.fieldError}>{fieldErrors.zip}</span>}
                              </div>
                              <button
                                type="button"
                                className="w-full rounded-lg px-4 py-2 text-center text-sm font-semibold text-white"
                                style={{
                                  border: "1px solid #ee4360",
                                  backgroundColor: isReturnAddressSaving ? "#f4a0ad" : "#ee4360",
                                  color: "#ffffff",
                                }}
                                onClick={handleReturnAddressSave}
                                disabled={isReturnAddressSaving}
                              >
                                {isReturnAddressSaving ? "Saving..." : "Save Address"}
                              </button>
                              {returnAddressError && (
                                <div className={classes.errorTooltip} role="alert" aria-live="assertive">
                                  {returnAddressError}
                                </div>
                              )}
                            </>
                          )}
                        </>
                      )}
                    </>
                  )}
                </div>
              )}
            </>
          )}
          {/* EDDM Address Box Toggle - Show for EDDM campaigns or when no specific subtype */}
          {(subtype === CAMPAIGN_SUBTYPES_ENUM.EDDM || (isPublicBuilder && isEDDMFormat(currentFormat))) && (
            <div
              className="flex items-center gap-2 justify-start text-[16px] max-h-[28px] min-h-[28px]"
              style={{ marginBottom: 8 }}
            >
              <div className="cursor-pointer flex items-center" onClick={handleEddmAddressBoxToggle}>
                {visibleType === "EDDM" ? <CheckedBoxIcon /> : <UncheckedBoxIcon />}
              </div>
              <div
                className="flex items-center gap-2 flex-1"
                style={{
                  color: "#2E3860",
                  fontFeatureSettings: "'liga' off, 'clig' off",
                  fontFamily: '"Inter", sans-serif',
                  fontSize: 12,
                  fontWeight: 400,
                  lineHeight: "20px",
                }}
              >
                <span
                  className={`cursor-pointer ${visibleType === "ADM" ? "text-gray-400" : ""}`}
                  onClick={handleEddmAddressBoxToggle}
                >
                  Display the address box
                </span>
              </div>
            </div>
          )}
        </>
      )}
      <div className={classes.backgroundSection} data-tour="background-colors">
        <h3 className={classes.subsectionTitle}>Background</h3>
        <div className={classes.backgroundItems}>
          <BackgroundColorPicker side="front" />
          <BackgroundColorPicker side="back" />
        </div>
      </div>
      {/* <div className="flex items-center justify-between text-[16px]" onClick={handleSnapMode}>
        <TldrawUiIcon className="h-6 w-6" icon={isSnapMode ? "toggle-on" : "toggle-off"} />
        Snap mode
      </div> */}
      <div className={classes.settings_row} data-tour="units">
        <label className={classes.label}>Unit</label>
        <Select
          variant="outlined"
          value={measurementUnit}
          onChange={(e) => handleUnitChange(String(e.target.value))}
          renderValue={() => displayUnitValue}
          className={classes.selectLikeFontSize}
          MenuProps={selectMenuProps}
          IconComponent={DropdownChevron}
          data-cypress_id="unit"
        >
          <MenuItem value="inches">Inches</MenuItem>
          <MenuItem value="mm">Mm</MenuItem>
        </Select>
      </div>
      <LinesHint />
    </div>
  );
};

export const Settings = React.memo(SettingsComponent);

const useStyles = makeStyles({
  settingsScroll: {
    overflowY: "auto" as any,
    overflowX: "hidden",
    flex: 1,
    minHeight: 0,
    scrollbarWidth: "none" as any,
    msOverflowStyle: "none" as any,
    "&::-webkit-scrollbar": {
      display: "none",
    },
  },
  root: {
    position: "absolute",
    left: "49px",
    top: "50px",
    bottom: "0px",
  },
  sidebar: {
    background: "var(--color-surface-panel)",
    width: 281,
    height: "100%",
    padding: 16,
    borderLeft: "1px solid var(--color-surface-canvas)",
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: 600,
    color: "var(--color-deep-blue)",
    marginBottom: 16,
  },
  backgroundSection: {
    marginBottom: 16,
    display: "flex",
    flexDirection: "column",
    gap: 16,
  },
  subsectionTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: "var(--color-deep-blue)",
    marginTop: 0,
  },
  backgroundItems: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  settings_row: {
    marginBottom: 16,
  },
  container: {
    background: "var(--color-surface-panel)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "space-between",
    width: 48,
    padding: 8,
  },
  items: {
    display: "grid",
    gap: 4,
    gridTemplateColumns: "repeat(1, minmax(0, 1fr))",
  },
  label: {
    display: "flex",
    fontSize: 14,
    lineHeight: "16px",
    color: "var(--color-deep-blue)",
    fontWeight: 600,
    marginBottom: 4,
    alignItems: "center",
  },
  selectLikeFontSize: {
    width: "100%",
    maxHeight: "38px !important",
    borderRadius: "8px !important",
    "& .MuiInputBase-root": {
      height: "38px !important",
      minHeight: "38px !important",
      maxHeight: "38px !important",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "#E2E2FC !important",
      borderWidth: "1px !important",
      transition: "none !important",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "#E2E2FC !important",
      borderWidth: "1px !important",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#E2E2FC !important",
      borderWidth: "1px !important",
      boxShadow: "none !important",
    },
    "& fieldset": {
      borderColor: "#E2E2FC !important",
      borderWidth: "1px !important",
      transition: "none !important",
    },
    "&:hover fieldset": {
      borderColor: "#E2E2FC !important",
      borderWidth: "1px !important",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#E2E2FC !important",
      borderWidth: "1px !important",
      boxShadow: "none !important",
    },
    "&:hover": {
      backgroundColor: "transparent !important",
    },
    "&.Mui-focused": {
      backgroundColor: "transparent !important",
      boxShadow: "none !important",
    },
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px !important",
      fontSize: "12px",
      color: "#2E3860",
      height: "38px !important",
      minHeight: "38px !important",
      maxHeight: "38px !important",
      overflow: "hidden",
    },
    "& .MuiSelect-root": {
      paddingLeft: "12px",
      paddingRight: "32px",
      height: "38px !important",
      minHeight: "38px !important",
      maxHeight: "38px !important",
    },
    "& .MuiSelect-select": {
      display: "flex",
      alignItems: "center",
      minHeight: "36px !important",
      maxHeight: "36px !important",
      overflow: "hidden",
      fontSize: "12px !important",
      "&:focus": {
        backgroundColor: "transparent !important",
      },
      "&:hover": {
        backgroundColor: "transparent !important",
      },
      "&:active": {
        backgroundColor: "transparent !important",
      },
    },
    "& .MuiSelect-icon": {
      color: "#2E3860",
      right: "8px",
    },
    "& .MuiOutlinedInput-input": {
      padding: "8px 12px",
    },
  },
  selectMenuPaper: {
    borderRadius: "8px",
    border: "1px solid #E1E6EB",
    boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.2)",
    marginTop: "-8px",
    "& .MuiMenuItem-root": {
      fontSize: "12px",
      position: "relative",
      paddingRight: "32px",
      "&:hover": {
        backgroundColor: "#E2E2FC",
      },
      "&.Mui-selected": {
        backgroundColor: "#FFFFFF !important",
        border: "1px solid #2E3860",
        borderRadius: "4px",
        "&::after": {
          content: '""',
          position: "absolute",
          right: "10px",
          top: "50%",
          transform: "translateY(-50%)",
          width: "12px",
          height: "12px",
          backgroundRepeat: "no-repeat",
          backgroundSize: "contain",
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12' fill='none'><path fill-rule='evenodd' clip-rule='evenodd' d='M3.54 4.86L2.7 5.7L5.4 8.4L11.4 2.4L10.56 1.56L5.4 6.72L3.54 4.86ZM10.8 6C10.8 8.64 8.64 10.8 6 10.8C3.36 10.8 1.2 8.64 1.2 6C1.2 3.36 3.36 1.2 6 1.2C6.48 1.2 6.9 1.26 7.32 1.38L8.28 0.42C7.56 0.18 6.78 0 6 0C2.7 0 0 2.7 0 6C0 9.3 2.7 12 6 12C9.3 12 12 9.3 12 6H10.8Z' fill='%2357578E'/></svg>\")",
        },
      },
      "&.Mui-selected:hover": {
        backgroundColor: "#FFFFFF !important",
      },
    },
  },
  required: {
    color: "red",
  },
  requiredTip: {
    color: "red",
    marginRight: "6px",
  },
  labelBlock: {
    display: "flex",
    justifyContent: "space-between",
  },
  infoIcon: {
    marginLeft: "8px",
  },
  tooltip: {
    backgroundColor: "#0C172C",
    color: "#FFFFFF",
    fontFamily: '"Inter", sans-serif',
    fontSize: 14,
    fontWeight: 700,
    lineHeight: "20px",
    maxWidth: 300,
    padding: "8px 16px",
    borderRadius: 4,
  },
  tooltipArrow: {
    color: "#0C172C",
  },
  returnAddressContainer: {
    position: "relative",
  },
  returnAddressFieldLabel: {
    color: "#0C172C",
    fontFeatureSettings: "'liga' off, 'clig' off",
    fontFamily: '"Inter", sans-serif',
    fontSize: 14,
    fontWeight: 600,
    lineHeight: "16px",
    marginBottom: 0,
    display: "flex",
    alignItems: "center",
  },
  returnAddressAutocomplete: {
    "& .MuiOutlinedInput-root": {
      height: "40px !important",
      alignItems: "center !important",
    },
    "& input": {
      fontSize: "12px !important",
      color: "#2E3860 !important",
    },
    "& input::placeholder": {
      fontSize: "12px !important",
      color: "#2E3860 !important",
      opacity: "1 !important",
    },
  },
  fieldError: {
    color: "#dc2626",
    fontSize: 11,
    marginTop: 2,
    lineHeight: 1.3,
  },
  errorTooltip: {
    marginTop: 4,
    borderRadius: 4,
    padding: "6px 10px",
    fontSize: 12,
    backgroundColor: "#fee2e2",
    color: "#b91c1c",
    border: "1px solid #fca5a5",
    boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
  },
});
